/**
 * 설정 항목 표.
 *
 * **이 배열이 설정 화면의 전부다** - 항목을 늘리는 일은 여기 한 줄을 더하는 일이고,
 * `ui/settings.ts`는 항목이 무엇인지 알지 못한다(아이콘 이름과 켬/끔 여부만 안다).
 *
 * 넷 다 **상태를 갖지 않는다.** 묻는 함수와 바꾸는 함수만 넘긴다 - 음소거의 원본은 오디오
 * 엔진, 배경음은 배경음 재생기, 진동은 햅틱 엔진, 언어는 사전 엔진이다. 여기에 사본을
 * 하나라도 두면 저장된 값과 화면이 갈릴 수 있는 창이 생기고, 그 창은 "껐는데 소리가 난다"로만
 * 드러난다. 그래서 이 파일은 모듈 상수 하나이고 앱에서 아무것도 받지 않는다 -
 * 받을 것이 있었다면 그건 이미 사본이다.
 *
 * 켜는 쪽으로 누른 클릭은 그 자체가 사용자 제스처다. 그래서 켤 때마다 해당 엔진을 함께
 * 해제한다. **타이틀이 첫 제스처를 보장하게 된 뒤에도 이 세 줄은 남긴다** - 여기서 하는 일이
 * "처음 여는 것"이 아니라 **다시 깨우는 것**이기 때문이다. AudioContext는 첫 해제 뒤에도
 * 정지될 수 있고(탭을 오래 묻어 두거나 OS가 오디오 포커스를 가져가면), 그때 `setMuted(false)`는
 * 컨텍스트를 깨우지 않는다 - 음소거만 풀고 노드를 정지된 시계 위에 예약한다.
 * `unlock()`이 그 자리에서 `resume()`을 부르는 유일한 경로이고, 이 클릭이 그것을 할 수 있는
 * 제스처다. 빠뜨리면 "껐다 켰더니 그때부터 조용하다"로 드러나고, 그건 버튼이 고장 난 것으로 읽힌다.
 */

import { bgm } from '../audio/bgm'
import { audio } from '../audio/engine'
import { haptics } from '../audio/haptics'
import { LOCALES, getLocale, setLocale, t, type Locale } from '../i18n'
import type { SettingsItem } from '../ui/settings'

/**
 * 다음 언어. 목록을 한 바퀴 돈다.
 *
 * `LOCALES`에서 계산하는 이유는 `{ ko: 'en', en: 'ko' }` 같은 표가 언어를 하나 더할 때
 * **조용히 맞는 채로 틀리기** 때문이다(세 번째 언어가 순환에서 빠져도 타입은 통과한다).
 */
function nextLocale(current: Locale): Locale {
  const index = LOCALES.indexOf(current)
  return LOCALES[(index + 1) % LOCALES.length] ?? current
}

/** 설정 항목 넷. 순서가 곧 화면의 순서다. */
export const SETTINGS_ITEMS: readonly SettingsItem[] = [
  {
    kind: 'toggle',
    id: 'sound',
    labelKey: 'settings.sound',
    icon: 'sound',
    isOn: () => !audio.isMuted(),
    toggle: () => {
      const muted = audio.toggleMute()
      if (!muted) audio.unlock()
      return !muted
    },
    /*
     * 효과음만 자기 접근성 문장을 쓴다. 음소거 버튼이 화면 구석에 혼자 서 있던 시절의 키를
     * 그대로 이어받은 것이고, 항목별 문장이 필요할 때 어떻게 넣는지의 예시이기도 하다.
     * 나머지 셋은 `{name}`을 받는 공용 문장으로 충분하다.
     */
    ariaOnKey: 'hud.sound.aria.on',
    ariaOffKey: 'hud.sound.aria.off',
  },
  {
    kind: 'toggle',
    id: 'bgm',
    labelKey: 'settings.bgm',
    icon: 'bgm',
    isOn: () => !bgm.isMuted(),
    toggle: () => {
      const muted = bgm.toggleMute()
      if (!muted) bgm.unlock()
      return !muted
    },
  },
  {
    kind: 'toggle',
    id: 'haptics',
    labelKey: 'settings.haptics',
    icon: 'haptics',
    /*
     * 아이폰·데스크톱에는 진동 장치가 없다(`haptics.ts`의 지원 표). 그런 기기에서는
     * **줄을 아예 만들지 않는다** - 눌러도 아무 일이 없는 스위치는 고장으로 읽히고,
     * 고장으로 읽힌 화면은 나머지 항목까지 의심받는다.
     *
     * **판단은 `haptics.isSupported()` 하나로 끝난다.** 그 함수가 이미 터치 지점 수까지
     * 본다(데스크톱 크롬은 `navigator.vibrate`를 갖고 있으면서 아무것도 울리지 않는다).
     * 한때 이 자리에 같은 검사를 한 벌 더 두었는데, 같은 판정이 두 곳에 있으면 한쪽만
     * 고쳐지는 날 "설정에는 있는데 안 울리는" 줄이 생긴다.
     */
    isAvailable: () => haptics.isSupported(),
    isOn: () => haptics.isEnabled(),
    toggle: () => {
      const enabled = haptics.toggleEnabled()
      /*
       * 켜면 그 자리에서 한 번 울린다. 이 항목만 **확인할 방법이 없기 때문**이다 -
       * 소리는 다음 발사에서 들리고 언어는 즉시 글자가 바뀌지만, 진동은 켠 것이 먹혔는지
       * 알 길이 없다. 짧은 정답 진동 하나가 그 답이다.
       */
      if (enabled) {
        haptics.unlock()
        haptics.vibrate('hit')
      }
      return enabled
    },
  },
  {
    kind: 'choice',
    id: 'language',
    labelKey: 'settings.language',
    icon: 'lang',
    value: () => t(`lang.${getLocale()}`),
    nextValue: () => t(`lang.${nextLocale(getLocale())}`),
    advance: () => setLocale(nextLocale(getLocale())),
  },
]
